import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Upload, Loader2, Save, X, Video, RefreshCw } from 'lucide-react';
import { adminApi, BACKEND_URL } from '../lib/api';

const CATEGORIES = ['REPORTAGE', 'LE TERRITOIRE', 'BON PLAN'];
const emptyForm = { title: '', category: 'REPORTAGE', description: '', duration_min: 3, order: 0 };

const LagoonCardsManagement = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRefs = useRef({});

  const load = async () => {
    try { setCards(await adminApi.listLagoonCards()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  const createCard = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await adminApi.createLagoonCard({ ...form, duration_min: parseInt(form.duration_min) || 3, order: parseInt(form.order) || 0 });
      setForm(emptyForm); setCreating(false); await load();
    } finally { setSaving(false); }
  };

  const upload = async (card, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { alert('Vidéo trop lourde (max 25 Mo)'); return; }
    setUploadingId(card.id); setUploadPct(0);
    try {
      await adminApi.uploadLagoonVideo(card.id, file, setUploadPct);
      // Refresh to show processing then compressed url
      await load();
    } catch (e) {
      alert('Erreur upload : ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingId(null); setUploadPct(0);
      if (fileRefs.current[card.id]) fileRefs.current[card.id].value = '';
    }
  };

  const del = async (card) => {
    if (!window.confirm(`Supprimer la carte « ${card.title} » ?`)) return;
    await adminApi.deleteLagoonCard(card.id);
    await load();
  };

  const saveField = async (card, patch) => {
    await adminApi.updateLagoonCard(card.id, patch);
    await load();
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;

  return (
    <div className="space-y-4" data-testid="lagoon-cards-mgmt">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Carte lagunaire — Vidéos</h3>
          <p className="text-sm text-slate-500">Chaque carte est publiée dans la section "Notre carte lagunaire" du site</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-full text-sm font-semibold hover:border-[#F4A340]"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setCreating(true)} data-testid="lagoon-new-btn" className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white rounded-full text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nouvelle carte
          </button>
        </div>
      </div>

      {creating && (
        <div className="bg-white rounded-2xl border-2 border-[#F4A340]/30 p-5 space-y-3" data-testid="lagoon-new-form">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-slate-900">Nouvelle carte</h4>
            <button onClick={() => setCreating(false)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <input placeholder="Titre *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Durée (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          </div>
          <textarea placeholder="Description courte" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={createCard} disabled={saving || !form.title.trim()} className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Créer
            </button>
          </div>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Video className="w-12 h-12 mx-auto opacity-40 mb-3" />
          Aucune carte pour le moment. Cliquez sur "Nouvelle carte" pour commencer.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => {
            const url = card.video_url && !card.video_url.startsWith('http') ? `${BACKEND_URL}${card.video_url}` : card.video_url;
            const isProcessing = card.processing;
            return (
              <div key={card.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-video bg-slate-100 relative">
                  {isProcessing ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-slate-500">
                      <Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" />
                      Compression en cours…
                    </div>
                  ) : url ? (
                    <video src={url} controls muted className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">Aucune vidéo</div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <input
                    value={card.title}
                    onChange={(e) => setCards(cards.map((c) => c.id === card.id ? { ...c, title: e.target.value } : c))}
                    onBlur={(e) => e.target.value !== card.title && saveField(card, { title: e.target.value })}
                    className="w-full font-bold text-slate-900 outline-none bg-transparent border-b border-transparent focus:border-[#F4A340] py-1"
                  />
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <select
                      value={card.category}
                      onChange={(e) => saveField(card, { category: e.target.value })}
                      className="px-2 py-1 rounded-full bg-slate-100 font-semibold text-slate-700 text-[10px]"
                    >
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <input
                      type="number" min="1" max="60"
                      defaultValue={card.duration_min}
                      onBlur={(e) => parseInt(e.target.value) !== card.duration_min && saveField(card, { duration_min: parseInt(e.target.value) || 3 })}
                      className="w-14 px-2 py-1 rounded bg-slate-100 text-slate-700"
                    /> <span className="text-slate-400">min</span>
                  </div>
                  <textarea
                    defaultValue={card.description}
                    onBlur={(e) => e.target.value !== card.description && saveField(card, { description: e.target.value })}
                    placeholder="Description..."
                    rows={2}
                    className="w-full text-xs text-slate-600 outline-none bg-transparent border border-transparent focus:border-slate-200 rounded p-1 resize-none"
                  />
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                    <input
                      ref={(el) => { fileRefs.current[card.id] = el; }}
                      type="file"
                      accept="video/*"
                      onChange={(e) => upload(card, e.target.files?.[0])}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileRefs.current[card.id]?.click()}
                      disabled={uploadingId === card.id || isProcessing}
                      data-testid={`lagoon-upload-${card.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-xs font-semibold"
                    >
                      {uploadingId === card.id ? <><Loader2 className="w-3 h-3 animate-spin" /> {uploadPct}%</> : <><Upload className="w-3 h-3" /> {url ? 'Remplacer' : 'Téléverser'}</>}
                    </button>
                    <button onClick={() => del(card)} className="p-1.5 rounded-full text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LagoonCardsManagement;
